import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { CATEGORIES } from "../lib/constants";
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronDown, ChevronRight, LogOut, LayoutDashboard, Sparkles } from "lucide-react";
import { Input } from "./ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export const BrandLogo = ({ className = "" }) => (
  <Link to="/" data-testid="navbar-brand-logo" className={`flex items-center gap-2.5 group ${className}`}>
    <img
      src="/brand/bull-mark.png"
      alt="Bull Creations"
      className="h-10 w-auto transition-transform duration-300 group-hover:scale-110"
    />
    <div className="leading-none">
      <div className="font-display font-extrabold text-lg tracking-tight text-[#1D3557] dark:text-white">
        BULL<span className="text-[#E63946]">CREATIONS</span>
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-[0.25em] text-slate-400">
        Unleash Your Everyday
      </div>
    </div>
  </Link>
);

export default function Navbar() {
  const {
    user,
    logout,
    currency,
    setCurrency,
    currencies,
    cartCount,
    setCartOpen,
    setAuthModalOpen,
    wishlist,
    settings,
  } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* utility bar */}
      <div className="bg-[#1D3557] text-white text-xs py-1.5 text-center font-medium tracking-wide">
        {settings?.announcement || "🚚 Free shipping across India & GCC on orders over ₹1999 • Crafted in Tiruppur"}
      </div>

      <div className="backdrop-blur-md bg-white/90 dark:bg-slate-900/90 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-4">
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden p-2 -ml-2"
                onClick={() => setMobileOpen((o) => !o)}
                data-testid="mobile-menu-toggle"
                aria-label="menu"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              <BrandLogo />
            </div>

            {/* desktop search */}
            <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                data-testid="search-input-field"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search for t-shirts, kurtis, hoodies..."
                className="pl-10 rounded-full bg-slate-100 dark:bg-slate-800 border-transparent focus-visible:ring-[#E63946]"
              />
            </form>

            <div className="flex items-center gap-1 sm:gap-2">
              {/* currency */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="currency-selector-dropdown"
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <span>{currencies[currency].flag}</span>
                  <span className="hidden sm:inline">{currency}</span>
                  <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {Object.values(currencies).map((c) => (
                    <DropdownMenuItem
                      key={c.code}
                      data-testid={`currency-option-${c.code}`}
                      onClick={() => setCurrency(c.code)}
                      className="cursor-pointer gap-2"
                    >
                      <span>{c.flag}</span> {c.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Link
                to="/wishlist"
                data-testid="navbar-wishlist-link"
                className="relative p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <Heart size={20} />
                {wishlist.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#E63946] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {wishlist.length}
                  </span>
                )}
              </Link>

              <button
                data-testid="cart-drawer-toggle"
                onClick={() => setCartOpen(true)}
                className="relative p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <ShoppingBag size={20} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#E63946] text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#1D3557] text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <div className="px-2 py-1.5 text-sm">
                      <div className="font-semibold truncate">{user.name}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/orders")} className="cursor-pointer" data-testid="menu-my-orders">
                      <ShoppingBag size={16} className="mr-2" /> My Orders
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/wallet")} className="cursor-pointer" data-testid="menu-bull-family">
                      <Sparkles size={16} className="mr-2 text-[#D4AF37]" /> Bull Family
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer" data-testid="admin-dashboard-link">
                        <LayoutDashboard size={16} className="mr-2" /> Admin Dashboard
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="logout-button">
                      <LogOut size={16} className="mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button
                  data-testid="navbar-login-button"
                  onClick={() => setAuthModalOpen(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1D3557] text-white text-sm font-semibold hover:bg-[#162943] transition-colors"
                >
                  <User size={16} /> <span className="hidden sm:inline">Login</span>
                </button>
              )}
            </div>
          </div>

          {/* category nav desktop */}
          <nav className="hidden lg:flex items-center gap-6 h-11 text-sm font-semibold">
            {[{ l: "MEN", g: "Men" }, { l: "WOMEN", g: "Women" }, { l: "KIDS", g: "Kids" }].map((m) => (
              <div key={m.g} className="relative group h-11 flex items-center">
                <Link to={`/shop?gender=${m.g}`} data-testid={`category-filter-${m.g.toLowerCase()}`} className="hover:text-[#E63946] transition-colors flex items-center gap-1">
                  {m.l}
                </Link>
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                  <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-800 p-3 w-56">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 pb-2">{m.l} · Shop by Category</div>
                    {CATEGORIES.map((c) => (
                      <Link
                        key={c.key}
                        to={`/shop?gender=${m.g}&category=${c.key}`}
                        data-testid={`mega-${m.g.toLowerCase()}-${c.key.toLowerCase()}`}
                        className="flex items-center justify-between px-3 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-[#E63946]/10 hover:text-[#E63946] transition-colors"
                      >
                        {c.label}
                        <ChevronRight size={14} className="opacity-0 group-hover:opacity-100" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {CATEGORIES.map((c) => (
              <Link
                key={c.key}
                to={`/shop?category=${c.key}`}
                data-testid={`nav-category-${c.key.toLowerCase()}`}
                className="text-slate-600 dark:text-slate-300 hover:text-[#E63946] transition-colors"
              >
                {c.label}
              </Link>
            ))}
            <Link to="/try-on" data-testid="nav-tryon" className="ml-auto flex items-center gap-1 text-[#E63946] font-bold">
              <Sparkles size={15} /> Virtual Try-On
            </Link>
            <Link to="/shop" className="text-[#D4AF37] font-bold">⚡ GCC Express</Link>
          </nav>
        </div>

        {/* mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 px-4 py-4 space-y-3 bg-white dark:bg-slate-900">
            <form onSubmit={submitSearch} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="pl-10 rounded-full bg-slate-100"
              />
            </form>
            <div className="grid grid-cols-2 gap-2 text-sm font-semibold">
              <Link to="/shop?gender=Men" onClick={() => setMobileOpen(false)} className="py-2">MEN</Link>
              <Link to="/shop?gender=Women" onClick={() => setMobileOpen(false)} className="py-2">WOMEN</Link>
              <Link to="/shop?gender=Kids" onClick={() => setMobileOpen(false)} className="py-2">KIDS</Link>
              {CATEGORIES.map((c) => (
                <Link key={c.key} to={`/shop?category=${c.key}`} onClick={() => setMobileOpen(false)} className="py-2 text-slate-600">
                  {c.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
